import React from "react";
import PropTypes from "prop-types";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import {
  BuildingIcon,
  DeleteIcon,
  DoctorIcon,
  InsuranceIcon,
  MedicalNotesIcon,
  PillIcon,
  PlusIcon,
  UserIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_THEME } from "../../../patientTheme";
import { Field, Section, fieldClass } from "../formLayout";
import {
  COMMON_HEADER_GENDER_OPTIONS,
  emptyDiagnosisRow,
  emptyMedicationRow,
} from "./ptNotesCommonHeaderUtils";

const DateFieldInput = ({ name, value, canEdit, printMode, onChange }) => {
  if (printMode) {
    return (
      <div className={`${fieldClass} flex items-center`}>
        {displayDateUS(value) || "—"}
      </div>
    );
  }
  return (
    <InputDatePicker
      name={name}
      formatSeparator="/"
      allowManualEntry
      disabled={!canEdit}
      value={formatDateForInput(value) || ""}
      onChange={(event) => onChange(event.target.value)}
      className="w-full"
      inputClassName={`${fieldClass} bg-white`}
    />
  );
};

DateFieldInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

/**
 * Fixed-order shared header for all Nurse Eval & Visit Notes forms.
 * Patient → Company → Insurance → Physician → Diagnosis → Medication.
 */
const PtNotesCommonHeader = ({
  value,
  canEdit = false,
  printMode = false,
  onChange,
}) => {
  const header = value || {};
  const diagnoses = Array.isArray(header.diagnoses) ? header.diagnoses : [];
  const medications = Array.isArray(header.medications)
    ? header.medications
    : [];

  const setField = (key, next) => {
    onChange({ ...header, [key]: next });
  };

  const updateDiagnosis = (rowId, patch) => {
    setField(
      "diagnoses",
      diagnoses.map((row) => (row.id === rowId ? { ...row, ...patch } : row)),
    );
  };

  const removeDiagnosis = (rowId) => {
    setField(
      "diagnoses",
      diagnoses.filter((row) => row.id !== rowId),
    );
  };

  const addDiagnosis = () => {
    setField("diagnoses", [...diagnoses, emptyDiagnosisRow()]);
  };

  const updateMedication = (rowId, patch) => {
    setField(
      "medications",
      medications.map((row) => (row.id === rowId ? { ...row, ...patch } : row)),
    );
  };

  const removeMedication = (rowId) => {
    setField(
      "medications",
      medications.filter((row) => row.id !== rowId),
    );
  };

  const addMedication = () => {
    setField("medications", [...medications, emptyMedicationRow()]);
  };

  return (
    <div className="space-y-3">
      <Section icon={UserIcon} title="1. Patient Information">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-2">
          <Field label="Patient Name" span="lg:col-span-1">
            <input
              type="text"
              className={fieldClass}
              value={header.patientName || ""}
              disabled={!canEdit}
              onChange={(e) => setField("patientName", e.target.value)}
            />
          </Field>
          <Field label="DOB">
            <DateFieldInput
              name="commonHeaderDob"
              value={header.dateOfBirth || ""}
              canEdit={canEdit}
              printMode={printMode}
              onChange={(v) => setField("dateOfBirth", v)}
            />
          </Field>
          <Field label="Gender">
            <select
              className={fieldClass}
              value={header.gender || ""}
              disabled={!canEdit}
              onChange={(e) => setField("gender", e.target.value)}
            >
              <option value="">Select…</option>
              {COMMON_HEADER_GENDER_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Address" span="sm:col-span-2">
            <input
              type="text"
              className={fieldClass}
              value={header.address || ""}
              disabled={!canEdit}
              onChange={(e) => setField("address", e.target.value)}
            />
          </Field>
          <Field label="Primary Phone Number">
            <input
              type="tel"
              className={fieldClass}
              value={header.primaryPhone || ""}
              disabled={!canEdit}
              placeholder="(000)000-0000"
              onChange={(e) => setField("primaryPhone", e.target.value)}
            />
          </Field>
        </div>
      </Section>

      <Section icon={BuildingIcon} title="2. Company Information">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-2">
          <Field label="Company Name" span="sm:col-span-2">
            <input
              type="text"
              className={fieldClass}
              value={header.companyName || ""}
              disabled={!canEdit}
              onChange={(e) => setField("companyName", e.target.value)}
            />
          </Field>
          <Field label="Phone">
            <input
              type="tel"
              className={fieldClass}
              value={header.companyPhone || ""}
              disabled={!canEdit}
              placeholder="(000)000-0000"
              onChange={(e) => setField("companyPhone", e.target.value)}
            />
          </Field>
          <Field label="Fax">
            <input
              type="tel"
              className={fieldClass}
              value={header.companyFax || ""}
              disabled={!canEdit}
              placeholder="(000)000-0000"
              onChange={(e) => setField("companyFax", e.target.value)}
            />
          </Field>
          <Field label="Address" span="sm:col-span-2 lg:col-span-4">
            <input
              type="text"
              className={fieldClass}
              value={header.companyAddress || ""}
              disabled={!canEdit}
              onChange={(e) => setField("companyAddress", e.target.value)}
            />
          </Field>
        </div>
      </Section>

      <Section icon={InsuranceIcon} title="3. Insurance Information">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
          <Field label="Insurance Name">
            <input
              type="text"
              className={fieldClass}
              value={header.insuranceName || ""}
              disabled={!canEdit}
              onChange={(e) => setField("insuranceName", e.target.value)}
            />
          </Field>
          <Field label="Policy Number">
            <input
              type="text"
              className={fieldClass}
              value={header.policyNumber || ""}
              disabled={!canEdit}
              onChange={(e) => setField("policyNumber", e.target.value)}
            />
          </Field>
        </div>
      </Section>

      <Section icon={DoctorIcon} title="4. Physician Information">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-2">
          <Field label="Physician Name" span="sm:col-span-2 lg:col-span-1">
            <input
              type="text"
              className={fieldClass}
              value={header.physicianName || ""}
              disabled={!canEdit}
              onChange={(e) => setField("physicianName", e.target.value)}
            />
          </Field>
          <Field label="NPI">
            <input
              type="text"
              className={fieldClass}
              value={header.physicianNpi || ""}
              disabled={!canEdit}
              onChange={(e) => setField("physicianNpi", e.target.value)}
            />
          </Field>
          <Field label="Phone">
            <input
              type="tel"
              className={fieldClass}
              value={header.physicianPhone || ""}
              disabled={!canEdit}
              placeholder="(000)000-0000"
              onChange={(e) => setField("physicianPhone", e.target.value)}
            />
          </Field>
          <Field label="Address" span="sm:col-span-2">
            <input
              type="text"
              className={fieldClass}
              value={header.physicianAddress || ""}
              disabled={!canEdit}
              onChange={(e) => setField("physicianAddress", e.target.value)}
            />
          </Field>
          <Field label="Fax">
            <input
              type="tel"
              className={fieldClass}
              value={header.physicianFax || ""}
              disabled={!canEdit}
              placeholder="(000)000-0000"
              onChange={(e) => setField("physicianFax", e.target.value)}
            />
          </Field>
        </div>
      </Section>

      <Section icon={MedicalNotesIcon} title="5. Diagnosis Information">
        <div className="overflow-x-auto rounded border border-slate-200">
          <table className="w-full min-w-[560px] text-left">
            <thead>
              <tr
                className={`text-[10px] uppercase tracking-wide ${PATIENT_THEME.tableHead.className}`}
              >
                <th className="px-2 py-1.5 font-semibold w-36">ICD-10 Code</th>
                <th className="px-2 py-1.5 font-semibold">Diagnosis</th>
                {canEdit && (
                  <th className="px-2 py-1.5 font-semibold w-12 text-right">
                    Action
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {diagnoses.length === 0 ? (
                <tr className="border-t border-slate-100">
                  <td
                    colSpan={canEdit ? 3 : 2}
                    className="px-2 py-3 text-[11px] text-slate-400 text-center"
                  >
                    No diagnoses on file
                  </td>
                </tr>
              ) : (
                diagnoses.map((row) => (
                  <tr key={row.id} className="border-t border-slate-100">
                    <td className="px-2 py-1">
                      <input
                        type="text"
                        className={fieldClass}
                        value={row.diagnosisCode || ""}
                        disabled={!canEdit}
                        placeholder="e.g. I10"
                        onChange={(e) =>
                          updateDiagnosis(row.id, {
                            diagnosisCode: e.target.value,
                          })
                        }
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="text"
                        className={fieldClass}
                        value={row.diagnosisName || ""}
                        disabled={!canEdit}
                        placeholder="Diagnosis description"
                        onChange={(e) =>
                          updateDiagnosis(row.id, {
                            diagnosisName: e.target.value,
                          })
                        }
                      />
                    </td>
                    {canEdit && (
                      <td className="px-2 py-1 text-right">
                        <button
                          type="button"
                          className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                          onClick={() => removeDiagnosis(row.id)}
                          title="Remove diagnosis"
                          aria-label="Remove diagnosis"
                        >
                          <DeleteIcon size={14} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={addDiagnosis}
            className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800"
          >
            <PlusIcon size={12} />
            Add diagnosis
          </button>
        )}
      </Section>

      <Section icon={PillIcon} title="6. Medication Information">
        <div className="overflow-x-auto rounded border border-slate-200">
          <table className="w-full min-w-[640px] text-left">
            <thead>
              <tr
                className={`text-[10px] uppercase tracking-wide ${PATIENT_THEME.tableHead.className}`}
              >
                <th className="px-2 py-1.5 font-semibold">Medication</th>
                <th className="px-2 py-1.5 font-semibold w-44">Dosage / Route</th>
                <th className="px-2 py-1.5 font-semibold w-36">Frequency</th>
                {canEdit && (
                  <th className="px-2 py-1.5 font-semibold w-12 text-right">
                    Action
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {medications.length === 0 ? (
                <tr className="border-t border-slate-100">
                  <td
                    colSpan={canEdit ? 4 : 3}
                    className="px-2 py-3 text-[11px] text-slate-400 text-center"
                  >
                    No medications on file
                  </td>
                </tr>
              ) : (
                medications.map((row) => (
                  <tr key={row.id} className="border-t border-slate-100">
                    <td className="px-2 py-1">
                      <input
                        type="text"
                        className={fieldClass}
                        value={row.medicationName || ""}
                        disabled={!canEdit}
                        onChange={(e) =>
                          updateMedication(row.id, {
                            medicationName: e.target.value,
                          })
                        }
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="text"
                        className={fieldClass}
                        value={row.dosage || ""}
                        disabled={!canEdit}
                        placeholder="e.g. 10mg PO"
                        onChange={(e) =>
                          updateMedication(row.id, { dosage: e.target.value })
                        }
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="text"
                        className={fieldClass}
                        value={row.frequency || ""}
                        disabled={!canEdit}
                        placeholder="e.g. BID"
                        onChange={(e) =>
                          updateMedication(row.id, {
                            frequency: e.target.value,
                          })
                        }
                      />
                    </td>
                    {canEdit && (
                      <td className="px-2 py-1 text-right">
                        <button
                          type="button"
                          className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                          onClick={() => removeMedication(row.id)}
                          title="Remove medication"
                          aria-label="Remove medication"
                        >
                          <DeleteIcon size={14} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={addMedication}
            className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800"
          >
            <PlusIcon size={12} />
            Add medication
          </button>
        )}
      </Section>
    </div>
  );
};

PtNotesCommonHeader.propTypes = {
  value: PropTypes.object,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

export default PtNotesCommonHeader;
